<template lang="pug">
.document-list
	.no-content(v-if='driveLinkState != "valid"')
		p.spacey {{ noContentMessage }}
	.content(v-else)
		.no-directories(v-if='!directories || !directories.length')
			p.spacey {{ "No folders to show" }}
		.directories(v-else)
			.directory(
				v-for='( directory, index ) in directories'
				:key="index"
			)
				.directory-name
					p {{ directory.folderUri }}
				.no-documents(v-if='!files[directory.folderUri] || !files[directory.folderUri].length')
					p.spacey {{ emptyMessage || "No Files To Show" }}
				.documents(v-else)
					.document(
						v-for='( file, index ) in files[directory.folderUri]'
						:key="index"
					)
						p {{ file.name }}
						.buttons-container(v-if="!confirm")
							.download-button.separate-gradient.right(	@click='openDocument( file.name, directory.folderUri )' )
								span.icon-export
							.delete-button.separate-gradient.right( @click='confirm = true' )
								span.icon-close
						.buttons-container.right(v-else)
							p Confirm Delete?
							.confirm.separate-gradient.right( @click="deleteDocument( assetKey, file.filesKey, directory.assetFoldersKey )" )
								span.icon-check
							.cancel.separate-gradient.right( @click="confirm = false" )
								span.icon-close
</template>

<script>
import Vue from 'vue';
import fs from 'fs';
import { DeleteDocument } from '@/lib/API';

export default {
	name : 'document-list',

	props : {
		documents : {
			type : Array
		},
		emptyMessage : {
			type : String
		},
		aFiles : {
			type : Array
		},
		assetKey : {
			type : Number
		}
	},

	data : () => ( {
		files   : {},
		confirm : false
	} ),

	computed : {
		driveLink() {
			return this.$store.getters.postgresUser.driveLink;
		},

		directories() {
			return this.documents.filter( a => a.folderUri || a.folderUri.trim() !== '' );
		},

		isElectron() {
			return this.$store.state.isElectron;
		},

		driveLinkState() {
			return this.$store.state.driveLinkState;
		},

		noContentMessage() {
			const { driveLinkState } = this;

			const messages = {
				'not-electron' : 'Switch to the Desktop app to view documents',
				'invalid'      : 'G Drive file path is invalid. Click settings in the side bar to change it.',
				'valid'        : ''
			};

			return messages[driveLinkState];
		}
	},

	watch : {
		directories : {
			immediate : true,
			handler( directories ) {
				if ( !directories ) {
					return;
				}

				if ( !directories.length ) {
					return;
				}

				if ( this.isElectron ) {
					this.readDirectories( directories );
				}
			}
		}
	},

	methods : {
		openDocument( document, directory ) {
			const filePath = `${directory}/${document}`;

			this.$store.dispatch( 'openDocument', filePath );
		},

		async deleteDocument( assetKey, filesKey, assetFoldersKey ) {
			const stateIndicator = await this.$store.dispatch( 'displayState', {
				message : 'Deleting Document...',
				state   : 'loading'
			} );

			const delDocPayload = { assetKey, assetFoldersKey };

			DeleteDocument( filesKey, delDocPayload )
				.then( () => stateIndicator.resolve( 'Deleted Successfully' ) )
				.then( () => this.$emit( 'close' ) )
				.catch( ( err ) => {
					console.error( err );
					stateIndicator.fail( { header : 'Didn\'t Delete Document', message : '' } );
				} );
		},

		readDirectories( directories ) {
			const { driveLink } = this;

			directories.forEach( ( directory ) => {
				const { folderUri } = directory;

				this.folderUri = folderUri;

				if ( !this.aFiles[0] ) {
					return;
				}

				/**
				 * @param {string} string - the string that we'll use to see if there is
				 * a slash at the beginning or not. If so we will remove the slash, if not
				 * we will keep the string how it is.
				 */
				const removeSurroundingSlashes = ( string ) => {
					let localString = string;

					// check if the first letter in the directory path is a slash, if so,
					// remove the slash so that we can combine the driveLink and the
					// folderUri variable to make "filePath"
					if ( localString.charAt( 0 ) === '/' || localString.charAt( 0 ) === '\\' ) {
						localString = localString.substring( 1 );
					}

					const lastCharacterIndex = localString.length - 1;
					const lastCharacter = localString.charAt( lastCharacterIndex );

					// check if the last letter in the directory path is a slash, if so,
					// remove the slash
					if ( lastCharacter === '/' || lastCharacter === '\\' ) {
						localString = localString.slice( 0, lastCharacterIndex );
					}

					return localString;
				};

				let filePath = `${driveLink}/${folderUri}`;

				// if this is a mac machine, we need to replace all \ to / in the
				// filePath
				if ( process.platform === 'darwin' ) {
					const splitPath = filePath.split( '\\' );

					filePath = splitPath.reduce( ( formattedString, partOfString ) => {
						let localString = formattedString;

						localString += `/${removeSurroundingSlashes( partOfString )}`;

						return localString;
					}, '' );
				}

				// on the contrary, if it's a windows machine we need to replace
				// all / to \ in the filePath
				if ( process.platform === 'win32' ) {
					const splitPath = filePath.split( '/' );

					filePath = splitPath.reduce( ( formattedString, partOfString ) => {
						let localString = formattedString;

						if ( partOfString.charAt( partOfString.length - 1 ) === ':' ) {
							return `${partOfString}`;
						}

						if ( !localString ) {
							return partOfString;
						}

						if ( !partOfString || partOfString === '' ) {
							return localString;
						}

						localString += `${removeSurroundingSlashes( partOfString )}\\`;

						return localString;
					}, '' );
				}

				if ( !fs.existsSync( filePath ) ) {
					return;
				}

				const files = fs.readdirSync( filePath );

				const sFiles = this.aFiles.filter( ( f ) => {
					const exp = files.filter( file => file === f.name );

					if ( exp[0] ) {
						return exp[0] === f.name;
					}
					return exp === f.name;
				} );

				Vue.set( this.files, folderUri, sFiles );
			} );
		}
	}
};
</script>

<style lang="scss">
.document-list {
	position: relative;
	display: flex;
	flex-flow: column;
	align-items: center;

	.no-content {
		width: 100%;

		p {
			text-align: center;
			font-size: 16px;
		}
	}

	.content {
		width: 100%;

		.no-directories {
			p {
				text-align: center;
			}
		}

		.directories {
			width: 100%;

			.directory {
				margin-bottom: 20px;

				.directory-name {
					margin-bottom: 10px;
					margin-left: 10px;

					p {
						color: $color-primary;
						letter-spacing: 2px;
					}
				}
			}
		}

		.no-documents {
			margin-left: 10px;
		}

		.document {
			display: flex;
			align-items: center;
			background: white;
			margin-bottom: 15px;
			border-radius: 10px;
			box-shadow: $secondaryBoxShadow;
			width: 100%;

			p {
				flex: 1 1 0;
			}

			> p,
			.download-button,
			.delete-button {
				padding: 10px;
			}

			.buttons-container {
				display: flex;
				align-items: center;
				justify-content: center;
				transition: transform 0.2s ease;
				height: 45px;

				p {
					padding-right: 15px;
				}
				.download-button,
				.delete-button,
				.confirm,
				.cancel {
					display: flex;
					flex-flow: row;
					align-items: center;
					padding: 0 25px;
					align-self: stretch;
					transform: scale(1, 1);
					transition: transform 0.2s ease;
					cursor: pointer;

					&:active,
					&:focus {
						transform: scale(0.8, 0.8) !important;
					}

					p {
						color: $blueGrey;
					}

					.icon-check {
						position: relative;
						display: inline-block;
						transform: translateY( -5px );
						height: 30px;
						width: 30px;
						&::before {
							position: absolute;
							left: 0;
							top: 60%;
							height: 40%;
							width: 3px;
							background-color: green;
							content: "";
							transform: translateX( 15px ) rotate(-50deg);
							transform-origin: left bottom;
						}
						&::after {
							position: absolute;
							left: 0;
							bottom: 0;
							height: 3px;
							width: 70%;
							background-color: green;
							content: "";
							transform: translateX( 15px ) rotate( -50deg );
							transform-origin: left bottom;
						}
					}
				}
			}
		}
	}
}
</style>
